import toast from 'react-hot-toast';
import { useEffect, useState } from 'react';
import { SignOutIcon, UserIcon } from '@phosphor-icons/react';
import { PageHeader } from 'components/PageHeader';

import { useAuth } from '../../../contexts/auth-context';
import { Breadcrumb } from '../../../components/Breadcrumb';
import { Button } from '../../../components/button/Button';
import { DataField } from '../../../components/DataField';
import { InputWrapper } from '../../../components/InputWrapper';
import { formatDateTime } from '../../../utils/date';
import { SimpleSelect } from '../../../components/select/SimpleSelect';
import { LANGUAGE_VALUES } from '../../../utils/language';
import { getDisplayError } from '../../../utils/get-display-error';
import { useTranslation } from '../../../contexts/translation-context';
import { Input } from '../../../components/input/Input';
import { PageHeading } from 'components/PageHeading';
import { updateMyProfile } from 'src/app/user/models';
import { Language } from 'src/types/enums';
import { captureException } from '@sentry/react';

/** Fields sit in one 420px column beside the avatar. */
const FIELD_CLASSNAME = 'w-[420px] max-w-full';

export const CustomerPortalMePage = () => {
  const { me, logout, refreshMe } = useAuth();
  const { setLanguage, i18n } = useTranslation();
  const [nameValue, setNameValue] = useState(me.name);
  const [lastPersistedName, setLastPersistedName] = useState(me.name);

  useEffect(() => {
    setNameValue(me.name);
    setLastPersistedName(me.name);
  }, [me.name]);

  const persistNameChange = async () => {
    const trimmedValue = nameValue.trim();
    if (!trimmedValue || lastPersistedName === trimmedValue) {
      return;
    }

    try {
      await updateMyProfile({
        name: trimmedValue,
      });
      setLastPersistedName(trimmedValue);
      refreshMe();
      toast.success(i18n('myProfile.nameChanged'));
    } catch (err) {
      captureException(err);
      console.log(err);
      toast.error(
        i18n('myProfile.nameNotChanged', {
          error: getDisplayError(err),
        }),
      );
    }
  };

  return (
    <div>
      <PageHeader title={me.name} />

      <PageHeading
        noMarginBottom
        leftSide={<Breadcrumb currentItem={i18n('myProfile.myProfile')} />}
        rightSide={
          <Button color="danger" iconLeft={<SignOutIcon className="button-icon" />} onTrigger={() => logout()}>
            {i18n('myProfile.logout')}
          </Button>
        }
      />

      <div className="p-7">
        <div className="card flex gap-8 p-5">
          {me.image ? (
            <img
              src={me.image}
              alt={me.name}
              className="h-24 w-24 shrink-0 rounded-md border border-border object-cover"
            />
          ) : (
            <div className="flex h-24 w-24 shrink-0 items-center justify-center rounded-md border border-border bg-surface-sunken">
              <UserIcon className="h-[34px] w-[34px] text-fg-muted" />
            </div>
          )}

          <div className="flex min-w-0 flex-col gap-3.5">
            <div className={FIELD_CLASSNAME}>
              <InputWrapper labelText={i18n('myProfile.name')} hideInvalid>
                <Input
                  type="text"
                  value={nameValue}
                  onChange={(newValue) => {
                    setNameValue(newValue);
                  }}
                  onBlur={() => {
                    persistNameChange();
                  }}
                  onKeyDown={(evt) => {
                    if (evt.key === 'Enter') {
                      persistNameChange();
                    }
                  }}
                />
              </InputWrapper>
            </div>

            <div className={FIELD_CLASSNAME}>
              <DataField title={i18n('myProfile.email')}>{me.email}</DataField>
            </div>

            <div className={FIELD_CLASSNAME}>
              <InputWrapper labelText={i18n('myProfile.language')} hideInvalid>
                <SimpleSelect
                  items={LANGUAGE_VALUES}
                  selectedItem={LANGUAGE_VALUES.find((v) => v.key === me.language) ?? null}
                  onSelect={async (newValue) => {
                    if (!newValue?.key) return;

                    try {
                      await updateMyProfile({
                        language: newValue.key as Language,
                      });
                      setLanguage(newValue.key);
                      refreshMe();
                      toast.success(i18n('myProfile.languageChanged'));
                    } catch (err) {
                      captureException(err);
                      console.log(err);
                      toast.error(
                        i18n('myProfile.languageNotChanged', {
                          error: getDisplayError(err),
                        }),
                      );
                    }
                  }}
                />
              </InputWrapper>
            </div>

            <div className={FIELD_CLASSNAME}>
              <DataField title={i18n('myProfile.joinedAt')}>{formatDateTime(me.createdAt)}</DataField>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
